import { useCallback, useRef } from "react";
import { StoreKey } from "src/lib/Intl";
import { storeConfigs } from "src/utils/storeConfig";
import { Cart, CartUpdateAction } from "src/types/ctgraphql.d";
import { useUpdateCart } from "src/hooks/useUpdateCart";

interface ValidateCartCountryHook {
  validateCartCountry: (cart?: Cart | null) => Promise<void>;
}

interface ResolveCountryResetArgs {
  storeKey?: string | null;
  shippingCountry?: string | null;
}

const getCountryForStoreKey = (storeKey?: string | null): string | null => {
  if (!storeKey) {
    return null;
  }

  return storeConfigs[storeKey as StoreKey]?.countryCode ?? null;
};

const resolveCountryReset = (
  args: ResolveCountryResetArgs
): CartUpdateAction[] | null => {
  const { storeKey, shippingCountry } = args;

  const storeCountry = getCountryForStoreKey(storeKey);

  if (!storeCountry || !shippingCountry) {
    return null;
  }

  if (shippingCountry === storeCountry) {
    return null;
  }

  return [
    { setShippingMethod: { shippingMethod: null } },
    { setCountry: { country: storeCountry } },
    { setShippingAddress: { address: { country: storeCountry } } },
    { setBillingAddress: { address: { country: storeCountry } } },
    { recalculate: { updateProductData: true } },
  ];
};

export const useValidateCartCountry = (): ValidateCartCountryHook => {
  const { updateCart } = useUpdateCart();
  const validatedCartRef = useRef<string | null>(null);

  const validateCartCountry = useCallback(
    async (cart?: Cart | null) => {
      if (!cart?.id) {
        return;
      }

      const cartVersionKey = `${cart.id}:${cart.version as number}`;

      if (validatedCartRef.current === cartVersionKey) {
        return;
      }

      validatedCartRef.current = cartVersionKey;

      const actions = resolveCountryReset({
        storeKey: cart.store?.key,
        shippingCountry: cart.shippingAddress?.country as
          | string
          | null
          | undefined,
      });

      if (!actions) {
        return;
      }

      const cartInfo = {
        id: cart.id,
        version: cart.version as number,
      };

      await updateCart({ cartInfo, actions });
    },
    [updateCart]
  );

  return { validateCartCountry };
};

export { resolveCountryReset, getCountryForStoreKey };
